import React from "react";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Shield, Database, Lock, Clock, Mail, Phone, Cookie, ShieldCheck, FileText, Megaphone, Users, RefreshCw } from "lucide-react";

const Section = ({ icon: Icon, number, title, children }) => (
  <div>
    <h2 className="text-2xl font-serif-display font-semibold mb-4 flex items-center gap-3" style={{ color: '#721111' }}>
      {Icon && <Icon className="h-6 w-6 flex-shrink-0" style={{ color: '#d4af37' }} />}
      {number}. {title}
    </h2>
    {children}
  </div>
);

const BulletList = ({ items }) => (
  <ul className="space-y-2" style={{ color: '#4a3728' }}>
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2">
        <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: '#721111' }}></span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
      <Navbar />

      <section
        className="py-16"
        style={{ background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)' }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Shield className="h-16 w-16 mx-auto mb-4" style={{ color: '#d4af37' }} />
            <h1 className="text-4xl sm:text-5xl font-serif-display font-bold mb-4" style={{ color: '#d4af37' }} data-testid="privacy-title">
              Personvernerklæring
            </h1>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg p-8 sm:p-12 space-y-10"
            style={{ border: '2px solid #d4af37' }}
          >
            {/* 1. Behandlingsansvarlig */}
            <Section icon={Database} number={1} title="Behandlingsansvarlig">
              <p className="mb-4" style={{ color: '#4a3728' }}>
                Masala Indisk Restaurant og Bar er behandlingsansvarlig for personopplysninger som samles inn via denne nettsiden.
              </p>
              <div className="p-4 rounded-lg" style={{ background: '#f4e7d8' }}>
                <p className="font-semibold mb-2" style={{ color: '#2d1810' }}>Kontaktinformasjon:</p>
                <div className="space-y-1" style={{ color: '#4a3728' }}>
                  <p className="flex items-center gap-2"><Mail className="h-4 w-4" style={{ color: '#d4af37' }} /> E-post: post@masalaindisk.no</p>
                  <p className="flex items-center gap-2"><Phone className="h-4 w-4" style={{ color: '#d4af37' }} /> Telefon: +47 48 91 51 90</p>
                  <p>Adresse: Rikard Kaarbøs plass 1, 9405 Harstad</p>
                </div>
              </div>
            </Section>

            <hr style={{ borderColor: '#d4af37', opacity: 0.3 }} />

            {/* 2. Hvilke opplysninger vi samler inn */}
            <Section icon={FileText} number={2} title="Hvilke opplysninger vi samler inn">
              <p className="mb-4" style={{ color: '#4a3728' }}>
                Vi samler inn minimalt med opplysninger, kun det som er strengt nodvendig for a levere vare tjenester:
              </p>

              <h3 className="text-lg font-semibold mt-4 mb-3" style={{ color: '#2d1810' }}>Ved bestilling eller reservasjon:</h3>
              <BulletList items={[
                "Navn",
                "Telefonnummer",
                "E-postadresse",
                "Bestillingsinformasjon (valgte retter, hentetid)"
              ]} />
              <div className="mt-4 p-4 rounded-lg" style={{ background: '#dcfce7', border: '1px solid #22c55e' }}>
                <p className="font-semibold mb-1" style={{ color: '#166534' }}>
                  Viktig: Automatisk sletting av data
                </p>
                <p style={{ color: '#166534' }}>
                  Alle bestillings- og reservasjonsdata (navn, telefonnummer, e-post, bestillingsdetaljer) slettes automatisk etter dagens dato. Vi lagrer ingen historiske kundedata.
                </p>
              </div>

              <h3 className="text-lg font-semibold mt-6 mb-3" style={{ color: '#2d1810' }}>Ved samtykke til markedsforing:</h3>
              <BulletList items={[
                "Kun e-postadresse — ingen andre opplysninger lagres"
              ]} />
              <p className="mt-2 text-sm" style={{ color: '#8b4513' }}>
                Vi lagrer ikke navn, telefonnummer eller andre personopplysninger for markedsforingsformol.
              </p>
            </Section>

            <hr style={{ borderColor: '#d4af37', opacity: 0.3 }} />

            {/* 3. Hva vi bruker opplysningene til */}
            <Section icon={Lock} number={3} title="Hva vi bruker opplysningene til">
              <p className="mb-4" style={{ color: '#4a3728' }}>Vi bruker dine opplysninger til:</p>
              <BulletList items={[
                "Behandle og levere bestillinger",
                "Kontakte deg om din bestilling (e-postvarsler)",
                "Sende tilbud og kampanjer via e-post (kun med ditt samtykke)"
              ]} />
            </Section>

            <hr style={{ borderColor: '#d4af37', opacity: 0.3 }} />

            {/* 4. Markedsføring via e-post */}
            <Section icon={Megaphone} number={4} title="Markedsforing via e-post">
              <p className="mb-4" style={{ color: '#4a3728' }}>
                Vi sender kun markedsforing via e-post. Vi bruker ikke SMS eller andre kanaler.
              </p>
              <p className="mb-4" style={{ color: '#4a3728' }}>
                Din e-postadresse lagres for markedsforing kun dersom:
              </p>
              <BulletList items={[
                "Du aktivt samtykker ved a velge \"Ja, gjerne!\" ved bestilling eller reservasjon"
              ]} />
              <div className="mt-4 p-4 rounded-lg" style={{ background: '#f4e7d8', border: '1px solid #d4af37' }}>
                <p className="font-semibold mb-2" style={{ color: '#2d1810' }}>Hva lagres ved samtykke:</p>
                <BulletList items={[
                  "Kun din e-postadresse",
                  "Ingen navn, telefonnummer eller andre opplysninger"
                ]} />
              </div>
              <p className="mt-6 mb-4" style={{ color: '#4a3728' }}>
                Du kan nar som helst melde deg av markedsforing ved a:
              </p>
              <BulletList items={[
                "Klikke pa \"avmeld\" i e-post",
                "Kontakte oss direkte pa post@masalaindisk.no"
              ]} />
              <p className="mt-4 font-semibold" style={{ color: '#4a3728' }}>
                Vi sender aldri markedsforing uten ditt aktive samtykke.
              </p>
            </Section>

            <hr style={{ borderColor: '#d4af37', opacity: 0.3 }} />

            {/* 5. Rettslig grunnlag */}
            <Section icon={ShieldCheck} number={5} title="Rettslig grunnlag">
              <p className="mb-4" style={{ color: '#4a3728' }}>
                Behandlingen av personopplysninger skjer på følgende grunnlag:
              </p>
              <ul className="space-y-3" style={{ color: '#4a3728' }}>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: '#721111' }}></span>
                  <span><strong>Avtale</strong> – for å gjennomføre bestillinger</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: '#721111' }}></span>
                  <span><strong>Samtykke</strong> – for markedsføring</span>
                </li>
              </ul>
            </Section>

            <hr style={{ borderColor: '#d4af37', opacity: 0.3 }} />

            {/* 6. Deling av opplysninger */}
            <Section icon={Users} number={6} title="Deling av opplysninger">
              <p className="mb-4" style={{ color: '#4a3728' }}>
                Vi deler kun dine opplysninger nar det er strengt nodvendig:
              </p>
              <BulletList items={[
                "Betalingsleverandor (Nets Easy) — for a behandle betaling",
                "E-postleverandor (Resend) — for a sende ordrebekreftelser"
              ]} />
              <p className="mt-4 font-semibold" style={{ color: '#4a3728' }}>
                Vi selger aldri dine data til tredjeparter. Alle samarbeidspartnere er forpliktet til a beskytte dine data.
              </p>
            </Section>

            <hr style={{ borderColor: '#d4af37', opacity: 0.3 }} />

            {/* 7. Lagringstid */}
            <Section icon={Clock} number={7} title="Lagringstid">
              <p className="mb-4" style={{ color: '#4a3728' }}>
                Vi lagrer personopplysninger sa kort som mulig:
              </p>
              <BulletList items={[
                "Bestillingsdata (navn, telefon, e-post, ordredetaljer): Slettes automatisk dagen etter bestillingen",
                "Reservasjonsdata: Slettes automatisk dagen etter reservasjonen",
                "E-post for markedsforing: Lagres til du melder deg av"
              ]} />
              <div className="mt-4 p-4 rounded-lg" style={{ background: '#dcfce7', border: '1px solid #22c55e' }}>
                <p className="font-semibold" style={{ color: '#166534' }}>
                  Automatisk sletting: Vart system sletter automatisk all bestillings- og reservasjonsdata daglig. Vi beholder ingen kundehistorikk.
                </p>
              </div>
            </Section>

            <hr style={{ borderColor: '#d4af37', opacity: 0.3 }} />

            {/* 8. Informasjonskapsler (Cookies) */}
            <Section icon={Cookie} number={8} title="Informasjonskapsler (Cookies)">
              <p className="mb-4" style={{ color: '#4a3728' }}>Vi bruker cookies for å:</p>
              <BulletList items={[
                "Sikre at nettsiden fungerer korrekt",
                "Forbedre brukeropplevelsen",
                "Analysere trafikk og bruk",
                "Tilpasse markedsføring"
              ]} />

              <h3 className="text-lg font-semibold mt-6 mb-3" style={{ color: '#2d1810' }}>Typer cookies:</h3>
              <BulletList items={[
                <><strong>Nødvendige cookies</strong> – kreves for at nettsiden skal fungere</>,
                <><strong>Analyse-cookies</strong> – hjelper oss å forstå bruk av nettsiden</>,
                <><strong>Markedsføringscookies</strong> – brukes til annonser og tilbud</>
              ]} />
              <p className="mt-4" style={{ color: '#4a3728' }}>
                Ved første besøk blir du bedt om å samtykke til bruk av cookies. Du kan når som helst endre eller trekke tilbake samtykket.
              </p>
            </Section>

            <hr style={{ borderColor: '#d4af37', opacity: 0.3 }} />

            {/* 9. Dine rettigheter */}
            <Section icon={ShieldCheck} number={9} title="Dine rettigheter">
              <p className="mb-4" style={{ color: '#4a3728' }}>I henhold til GDPR (EU forordning 2016/679) har du rett til a:</p>
              <BulletList items={[
                "Fa innsyn i dine opplysninger (Artikkel 15)",
                "Be om retting eller sletting (Artikkel 16 & 17)",
                "Trekke tilbake samtykke (Artikkel 7)",
                "Be om begrensning av behandling (Artikkel 18)",
                "Fa utlevert dine data i maskinlesbart format (Artikkel 20 - Dataportabilitet)",
                "Klage til Datatilsynet (datatilsynet.no)"
              ]} />
              <div className="mt-6 p-4 rounded-lg" style={{ background: '#dcfce7', border: '1px solid #22c55e' }}>
                <p className="font-semibold mb-2" style={{ color: '#166534' }}>Bruk dine rettigheter selv:</p>
                <p className="mb-3" style={{ color: '#166534' }}>
                  Du kan laste ned, slette eller melde deg av markedsforing direkte pa var selvbetjeningsside:
                </p>
                <a
                  href="/mine-rettigheter"
                  className="inline-block px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105"
                  style={{ background: '#166534', color: '#fff' }}
                  data-testid="gdpr-rights-link"
                >
                  Ga til Mine Rettigheter
                </a>
              </div>
            </Section>

            <hr style={{ borderColor: '#d4af37', opacity: 0.3 }} />

            {/* 10. Datasikkerhet */}
            <Section icon={Lock} number={10} title="Datasikkerhet">
              <p className="mb-4" style={{ color: '#4a3728' }}>
                Vi beskytter dine personopplysninger gjennom:
              </p>
              <BulletList items={[
                "Krypterte passord (bcrypt-hashing)",
                "Sikker tilgangskontroll til administrasjonspanel",
                "Automatisk daglig sletting av kundedata",
                "Sikkerhetslogg som overvaker alle innloggingsforsok",
                "Kryptert kommunikasjon (HTTPS/SSL)"
              ]} />
            </Section>

            <hr style={{ borderColor: '#d4af37', opacity: 0.3 }} />

            {/* 11. Endringer */}
            <Section icon={RefreshCw} number={11} title="Endringer">
              <p className="mb-4" style={{ color: '#4a3728' }}>
                Vi kan oppdatere denne personvernerklæringen ved behov. Oppdatert versjon vil alltid være tilgjengelig på nettsiden.
              </p>
              <div className="p-4 rounded-lg" style={{ background: '#f4e7d8' }}>
                <p className="font-semibold" style={{ color: '#2d1810' }}>
                  Sist oppdatert: {new Date().toLocaleDateString('nb-NO', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
            </Section>

            {/* Contact Box */}
            <div className="p-6 rounded-xl" style={{ background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)' }}>
              <h2 className="text-2xl font-serif-display font-semibold mb-4" style={{ color: '#d4af37' }}>
                Kontakt oss
              </h2>
              <p className="mb-4" style={{ color: '#f5e6d3' }}>
                Har du spørsmål om hvordan vi behandler dine personopplysninger, eller ønsker du å utøve dine rettigheter?
              </p>
              <div className="space-y-2">
                <p className="flex items-center gap-2" style={{ color: '#f5e6d3' }}>
                  <Mail className="h-5 w-5" style={{ color: '#d4af37' }} />
                  <a href="mailto:post@masalaindisk.no" className="hover:underline">post@masalaindisk.no</a>
                </p>
                <p className="flex items-center gap-2" style={{ color: '#f5e6d3' }}>
                  <Phone className="h-5 w-5" style={{ color: '#d4af37' }} />
                  <a href="tel:+4748915190" className="hover:underline">+47 48 91 51 90</a>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
